import { useMemo } from 'react';
import {
  DiscoveryApi,
  FetchApi,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  AccessResponse,
  AddSecretRequest,
  EditSecretRequest,
  RemoveSecretRequest,
  Stage,
  StageSecretsResponse,
  WriteResponse,
} from '@internal/backstage-plugin-keyvault-secrets-common';

/** Calls the `keyvault-secrets` backend with the user's Backstage token. */
export class KeyVaultSecretsClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  /** Any failure is "not allowed": the backend returns 404 when the feature
   * is off and 403 for non-admins. */
  async isAllowed(entityRef: string): Promise<boolean> {
    try {
      const body = await this.request<AccessResponse>('GET', 'access', entityRef);
      return body.allowed === true;
    } catch {
      return false;
    }
  }

  list(entityRef: string, stage: Stage): Promise<StageSecretsResponse> {
    return this.request('GET', `${stage}/secrets`, entityRef);
  }

  edit(
    entityRef: string,
    stage: Stage,
    vaultName: string,
    body: EditSecretRequest,
  ): Promise<WriteResponse> {
    return this.request('PUT', `${stage}/secrets/${encodeURIComponent(vaultName)}`, entityRef, body);
  }

  add(entityRef: string, stage: Stage, body: AddSecretRequest): Promise<WriteResponse> {
    return this.request('POST', `${stage}/secrets`, entityRef, body);
  }

  remove(
    entityRef: string,
    stage: Stage,
    vaultName: string,
    body: RemoveSecretRequest,
  ): Promise<WriteResponse> {
    return this.request(
      'POST',
      `${stage}/secrets/${encodeURIComponent(vaultName)}/remove`,
      entityRef,
      body,
    );
  }

  disable(entityRef: string, stage: Stage, vaultName: string): Promise<WriteResponse> {
    return this.request(
      'POST',
      `${stage}/secrets/${encodeURIComponent(vaultName)}/disable`,
      entityRef,
    );
  }

  enable(entityRef: string, stage: Stage, vaultName: string): Promise<WriteResponse> {
    return this.request(
      'POST',
      `${stage}/secrets/${encodeURIComponent(vaultName)}/enable`,
      entityRef,
    );
  }

  private async request<T>(
    method: 'GET' | 'PUT' | 'POST',
    path: string,
    entityRef: string,
    body?: object,
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('keyvault-secrets');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/${path}?entityRef=${encodeURIComponent(entityRef)}`,
      body
        ? {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        : { method },
    );
    if (!response.ok) {
      const error = await ResponseError.fromResponse(response);
      throw new Error(error.cause.message || error.message);
    }
    return (await response.json()) as T;
  }
}

export function useKeyVaultSecretsClient(): KeyVaultSecretsClient {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return useMemo(
    () => new KeyVaultSecretsClient(discoveryApi, fetchApi),
    [discoveryApi, fetchApi],
  );
}
